import { useEffect, useState } from 'react'
import { Button } from '@merels/ui'
import { useWebContainer } from '../../../../components/providers/DepProvider/DepProvider'

type Publisher = { publisherId: string; slug: string; displayName: string }
type Creator = { creatorId: string; displayName: string; creatorType: 'human' | 'ai' | 'service' }
type Grant = { publisherSlug: string; creatorId: string }
type Token = { tokenId: string; createdAt: string; status: 'active' | 'revoked' }

export function PublishingInfo() {
  const { auth, roomConnection } = useWebContainer()
  const [publishers, setPublishers] = useState<Publisher[]>([])
  const [creators, setCreators] = useState<Creator[]>([])
  const [grants, setGrants] = useState<Grant[]>([])
  const [publisherSlug, setPublisherSlug] = useState<string | null>(null)
  const [creatorId, setCreatorId] = useState<string | null>(null)
  const [tokens, setTokens] = useState<Token[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createPublisherOpen, setCreatePublisherOpen] = useState(false)
  const [editPublisherOpen, setEditPublisherOpen] = useState(false)
  const [publisherSwitchOpen, setPublisherSwitchOpen] = useState(false)
  const [createCreatorOpen, setCreateCreatorOpen] = useState(false)
  const [editCreatorOpen, setEditCreatorOpen] = useState(false)
  const [creatorSwitchOpen, setCreatorSwitchOpen] = useState(false)
  const [authorizeOpen, setAuthorizeOpen] = useState(false)
  const [publisherName, setPublisherName] = useState('')
  const [newPublisherSlug, setNewPublisherSlug] = useState('')
  const [creatorName, setCreatorName] = useState('')
  const [initialCreatorName, setInitialCreatorName] = useState('')
  const [existingCreatorId, setExistingCreatorId] = useState('')
  const isSignedIn = auth.isLoaded.value && !!auth.currentUser.value
  const workerUrl = roomConnection._workerUrl || ''

  const request = async (path: string, init?: RequestInit) => {
    const token = await auth.getToken()
    const headers = new Headers(init?.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (init?.body) headers.set('Content-Type', 'application/json')
    const response = await fetch(`${workerUrl}${path}`, { ...init, headers })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) throw new Error(data.error || 'Request failed')
    return data
  }

  const load = async () => {
    if (!isSignedIn) return
    setLoading(true)
    setError(null)
    try {
      const data = await request('/api/creator/publishing')
      const nextPublishers = data.publishers as Publisher[]
      const nextCreators = data.creators as Creator[]
      const nextGrants = data.grants as Grant[]
      setPublishers(nextPublishers)
      setCreators(nextCreators)
      setGrants(nextGrants)
      setPublisherSlug((current) => nextPublishers.some((publisher) => publisher.slug === current) ? current : nextPublishers[0]?.slug || null)
      setCreatorId((current) => nextCreators.some((creator) => creator.creatorId === current) ? current : null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load publishing settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [isSignedIn])

  const publisher = publishers.find((item) => item.slug === publisherSlug) || null
  const publisherCreators = publisher
    ? creators.filter((creator) => grants.some((grant) => grant.publisherSlug === publisher.slug && grant.creatorId === creator.creatorId))
    : []
  const selectedCreator = publisherCreators.find((creator) => creator.creatorId === creatorId) || publisherCreators[0] || null
  const availableCreators = creators.filter((creator) => !publisherCreators.some((publisherCreator) => publisherCreator.creatorId === creator.creatorId))

  useEffect(() => {
    if (selectedCreator) setCreatorId(selectedCreator.creatorId)
    else setCreatorId(null)
  }, [publisherSlug, selectedCreator?.creatorId])

  useEffect(() => {
    if (!selectedCreator) {
      setTokens([])
      return
    }
    void request(`/api/creator/token?creatorId=${encodeURIComponent(selectedCreator.creatorId)}`)
      .then((data) => setTokens(data.tokens || []))
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'Could not load token'))
  }, [selectedCreator?.creatorId])

  const createPublisher = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      const user = auth.currentUser.value
      const data = await request('/api/creator/publishers', { method: 'POST', body: JSON.stringify({ slug: newPublisherSlug, displayName: publisherName, defaultCreatorName: user?.fullName || user?.username || 'Creator', creatorName: publishers.length === 0 ? initialCreatorName : undefined }) })
      setPublisherSlug(data.publisher.slug)
      setCreatorId(data.creator.creatorId)
      setCreatePublisherOpen(false)
      setPublisherName('')
      setNewPublisherSlug('')
      setInitialCreatorName('')
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create publisher')
    }
  }

  const savePublisher = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!publisher) return
    try {
      await request(`/api/creator/publishers/${encodeURIComponent(publisher.slug)}`, { method: 'PATCH', body: JSON.stringify({ displayName: publisherName }) })
      setEditPublisherOpen(false)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update publisher')
    }
  }

  const saveCreator = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedCreator) return
    try {
      await request(`/api/creator/creators/${encodeURIComponent(selectedCreator.creatorId)}`, { method: 'PATCH', body: JSON.stringify({ displayName: creatorName }) })
      setEditCreatorOpen(false)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update creator')
    }
  }

  const createCreator = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!publisher) return
    try {
      const data = await request(`/api/creator/publishers/${encodeURIComponent(publisher.slug)}/creators/new`, { method: 'POST', body: JSON.stringify({ displayName: creatorName, creatorType: 'human' }) })
      setCreatorId(data.creator.creatorId)
      setCreatorName('')
      setCreateCreatorOpen(false)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create creator')
    }
  }

  const authorizeCreator = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!publisher || !existingCreatorId) return
    try {
      await request(`/api/creator/publishers/${encodeURIComponent(publisher.slug)}/creators`, { method: 'POST', body: JSON.stringify({ creatorId: existingCreatorId }) })
      setCreatorId(existingCreatorId)
      setExistingCreatorId('')
      setAuthorizeOpen(false)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not authorize creator')
    }
  }

  if (!isSignedIn) return <div className="p-4 text-xs text-muted">Sign in to set up publishing.</div>
  if (loading && publishers.length === 0) return <div className="p-4 text-xs text-muted">Loading publishing settings...</div>

  const publisherForm = <form onSubmit={createPublisher} className="space-y-2 border border-basic rounded p-3">
    <div className="text-sm font-semibold">{publishers.length === 0 ? 'Set up publishing' : 'Create publisher'}</div>
    <input required value={publisherName} onChange={(event) => setPublisherName(event.target.value)} placeholder="Publisher name" className="w-full bg-neutral-900 border border-basic rounded px-2 py-1.5 text-xs" />
    <input required value={newPublisherSlug} onChange={(event) => setNewPublisherSlug(event.target.value)} placeholder="Slug, e.g. merels" className="w-full bg-neutral-900 border border-basic rounded px-2 py-1.5 text-xs" />
    {publishers.length === 0 && <input required value={initialCreatorName} onChange={(event) => setInitialCreatorName(event.target.value)} placeholder="Creator name" className="w-full bg-neutral-900 border border-basic rounded px-2 py-1.5 text-xs" />}
    <Button type="submit" size="sm">{publishers.length === 0 ? 'Create a token' : 'Create publisher'}</Button>
  </form>

  if (publishers.length === 0) return <div className="p-4 space-y-3">{publisherForm}{error && <div className="text-xs text-red-400">{error}</div>}</div>

  return <div className="p-4 space-y-4 text-xs">
    {publisher && <>
      <div className="flex items-center justify-between gap-2"><div className="min-w-0 truncate"><span className="text-muted">Publisher: </span><span className="font-semibold">{publisher.displayName}</span> <span className="font-mono text-[10px] text-muted">({publisher.slug})</span></div><div className="flex gap-1"><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setPublisherName(publisher.displayName); setEditPublisherOpen(true) }}>Edit</Button><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => publishers.length > 1 ? setPublisherSwitchOpen(!publisherSwitchOpen) : setCreatePublisherOpen(true)}>{publishers.length > 1 ? 'Switch' : 'New'}</Button></div></div>
      {editPublisherOpen && <form onSubmit={savePublisher} className="flex gap-2"><input required value={publisherName} onChange={(event) => setPublisherName(event.target.value)} className="min-w-0 flex-1 bg-neutral-900 border border-basic rounded px-2 py-1" /><Button size="sm" type="submit">Save</Button></form>}
      {publisherSwitchOpen && <div className="space-y-1 border border-basic rounded p-2">{publishers.filter((item) => item.slug !== publisher.slug).map((item) => <button key={item.publisherId} className="block w-full text-left px-1 py-1 hover:bg-muted-bg/30" onClick={() => { setPublisherSlug(item.slug); setPublisherSwitchOpen(false) }}>{item.displayName} <span className="font-mono text-[10px] text-muted">({item.slug})</span></button>)}<button className="block px-1 py-1 text-accent" onClick={() => { setPublisherSwitchOpen(false); setCreatePublisherOpen(true) }}>Create new publisher</button></div>}
      {createPublisherOpen && publisherForm}
      <section className="space-y-2 border-t border-basic pt-3">
        {selectedCreator && <div className="flex items-center justify-between gap-2"><div className="min-w-0 truncate"><span className="text-muted">Creator: </span><span className="font-semibold">{selectedCreator.displayName}</span> <span className="text-[10px] text-muted">({selectedCreator.creatorType})</span></div><div className="flex gap-1"><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setCreatorName(selectedCreator.displayName); setEditCreatorOpen(true) }}>Edit</Button><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => publisherCreators.length > 1 ? setCreatorSwitchOpen(!creatorSwitchOpen) : setCreateCreatorOpen(true)}>{publisherCreators.length > 1 ? 'Switch' : 'New'}</Button></div></div>}
        {editCreatorOpen && <form onSubmit={saveCreator} className="flex gap-2"><input required value={creatorName} onChange={(event) => setCreatorName(event.target.value)} className="min-w-0 flex-1 bg-neutral-900 border border-basic rounded px-2 py-1" /><Button size="sm" type="submit">Save</Button></form>}
        {creatorSwitchOpen && selectedCreator && <div className="space-y-1 border border-basic rounded p-2">{publisherCreators.filter((creator) => creator.creatorId !== selectedCreator.creatorId).map((creator) => <button key={creator.creatorId} className="block w-full text-left px-1 py-1 hover:bg-muted-bg/30" onClick={() => { setCreatorId(creator.creatorId); setCreatorSwitchOpen(false) }}>{creator.displayName} <span className="text-[10px] text-muted">({creator.creatorType})</span></button>)}<button className="block px-1 py-1 text-accent" onClick={() => { setCreatorSwitchOpen(false); setCreateCreatorOpen(true) }}>Create new creator</button></div>}
        {publisherCreators.length === 0 && !createCreatorOpen && <Button size="sm" onClick={() => setCreateCreatorOpen(true)}>Add creator</Button>}
        {createCreatorOpen && <form onSubmit={createCreator} className="flex gap-2"><input required value={creatorName} onChange={(event) => setCreatorName(event.target.value)} placeholder="Creator name" className="min-w-0 flex-1 bg-neutral-900 border border-basic rounded px-2 py-1" /><Button size="sm" type="submit">Create</Button></form>}
        {availableCreators.length > 0 && <button className="block text-[10px] text-muted hover:text-primary" onClick={() => setAuthorizeOpen(!authorizeOpen)}>Authorize an existing creator</button>}
        {authorizeOpen && <form onSubmit={authorizeCreator} className="flex gap-2"><select required value={existingCreatorId} onChange={(event) => setExistingCreatorId(event.target.value)} className="min-w-0 flex-1 bg-neutral-900 border border-basic rounded px-2 py-1"><option value="">Select creator</option>{availableCreators.map((creator) => <option key={creator.creatorId} value={creator.creatorId}>{creator.displayName}</option>)}</select><Button size="sm" type="submit">Authorize</Button></form>}
      </section>
      {selectedCreator && <section className="space-y-2 border-t border-basic pt-3"><div className="font-semibold">Creator token</div>{tokens.map((token) => <div key={token.tokenId} className="flex items-center justify-between gap-2"><code className="min-w-0 truncate text-[10px]">{token.tokenId}</code><Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => void navigator.clipboard.writeText(token.tokenId)}>Copy</Button></div>)}</section>}
    </>}
    {error && <div className="text-xs text-red-400">{error}</div>}
  </div>
}
